import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Calculator, ShieldAlert, Info, ExternalLink } from 'lucide-react';
import BrandLink from '@/components/ui/BrandLink';
import Tagline from '@/components/ui/Tagline';

export default function Disclaimer() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <BrandLink to="/" />
          <a href="/" className="text-sm text-slate-500 hover:text-slate-700">
            Retour à l'accueil
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 md:p-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-amber-100 p-3 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Avertissement
              </h1>
              <p className="text-slate-500 mt-1">
                Conditions d'utilisation et limites de TrésoPilot
              </p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            {/* Préambule */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-8">
              <p className="text-amber-900 font-semibold mb-2">⚠️ Avertissement important</p>
              <p className="text-amber-800 leading-relaxed">
                TrésoPilot est un outil d'aide à la gestion de trésorerie. Il ne constitue en aucun cas un service 
                d'expertise-comptable, de conseil fiscal ou juridique. Les informations fournies sont indicatives et 
                ne sauraient remplacer l'avis d'un professionnel ou les informations officielles des organismes compétents.
              </p>
            </div>

            {/* Nature du service */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">Nature du service</h2>
              </div>
              
              <div className="bg-blue-50 rounded-xl p-6 mb-4">
                <p className="text-slate-700 leading-relaxed mb-3">
                  TrésoPilot est un <strong>outil d'aide à la gestion</strong> destiné aux micro-entrepreneurs français. 
                  Il propose :
                </p>
                <ul className="space-y-2 text-slate-700 list-disc pl-6">
                  <li>Des estimations de cotisations sociales et fiscales</li>
                  <li>Des rappels d'échéances URSSAF, TVA et CFE</li>
                  <li>Un suivi de trésorerie indicatif</li>
                  <li>Des outils de visualisation et d'organisation financière</li>
                </ul>
              </div>

              <p className="text-slate-700 leading-relaxed">
                <strong>Ce que TrésoPilot N'EST PAS :</strong>
              </p>
              <ul className="space-y-2 text-slate-700 list-disc pl-6 mt-2">
                <li>Un service de comptabilité ou d'expertise-comptable</li>
                <li>Un service de conseil fiscal ou juridique</li>
                <li>Un logiciel de déclaration officielle</li>
                <li>Un substitut aux portails officiels (URSSAF, impôts.gouv.fr)</li>
              </ul>
            </section>

            {/* Calculs et estimations */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Calculator className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">Calculs et estimations</h2>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                <p className="text-amber-900 font-semibold mb-2">⚠️ Nature indicative</p>
                <p className="text-amber-800 leading-relaxed">
                  Les montants affichés (URSSAF, impôts, TVA, CFE) sont des <strong>estimations indicatives</strong> 
                  calculées à partir des informations que vous avez renseignées et des paramètres par défaut de l'application.
                </p>
              </div>

              <div className="space-y-3">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-semibold text-slate-900 mb-2">Limites des calculs :</p>
                  <ul className="space-y-2 text-slate-700 list-disc pl-6">
                    <li>Ces calculs ne constituent <strong>en aucun cas</strong> une déclaration officielle</li>
                    <li>Ils ne remplacent pas vos obligations légales de déclaration</li>
                    <li>Les taux et règles fiscales peuvent varier selon votre situation personnelle</li>
                    <li>Des erreurs de saisie peuvent entraîner des estimations incorrectes</li>
                    <li>Les changements réglementaires récents peuvent ne pas être immédiatement répercutés</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-semibold text-slate-900 mb-2">Exactitude des données :</p>
                  <p className="text-slate-700 leading-relaxed">
                    L'exactitude des estimations dépend entièrement de la qualité et de la complétude des informations 
                    que vous saisissez (chiffre d'affaires, options fiscales, taux personnalisés, etc.). 
                    <strong> Vous êtes seul responsable</strong> de vérifier l'exactitude de ces informations.
                  </p>
                </div>
              </div>
            </section>

            {/* Échéances et rappels */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Échéances et rappels</h2>
              
              <p className="text-slate-700 leading-relaxed mb-4">
                Les dates d'échéances affichées sont calculées selon les règles générales applicables aux micro-entrepreneurs. 
                Cependant, <strong>votre situation personnelle</strong> peut nécessiter des dates différentes.
              </p>

              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <p className="text-red-900 font-semibold mb-2">🚨 Obligation de vérification</p>
                <p className="text-red-800 leading-relaxed">
                  <strong>Vous devez impérativement vérifier</strong> les dates exactes et les montants dus sur les 
                  portails officiels. Les rappels de TrésoPilot sont des aides mémoire et ne vous dégagent pas de 
                  votre responsabilité de respecter les échéances légales.
                </p>
              </div>

              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-slate-900 font-semibold mb-3">Consultez systématiquement les portails officiels :</p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <a href="https://www.autoentrepreneur.urssaf.fr" target="_blank" rel="noopener noreferrer" 
                       className="text-emerald-600 hover:text-emerald-700 underline">
                      autoentrepreneur.urssaf.fr
                    </a>
                    <span className="text-slate-600 text-sm">- pour vos déclarations URSSAF</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <a href="https://www.impots.gouv.fr" target="_blank" rel="noopener noreferrer" 
                       className="text-emerald-600 hover:text-emerald-700 underline">
                      impots.gouv.fr
                    </a>
                    <span className="text-slate-600 text-sm">- pour vos obligations fiscales et la TVA</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-slate-700">Votre centre de formalités des entreprises (CFE)</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Absence de conseil */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <ShieldAlert className="w-6 h-6 text-red-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">Absence de conseil professionnel</h2>
              </div>
              
              <div className="bg-red-50 rounded-xl p-6 mb-4">
                <p className="text-slate-700 leading-relaxed mb-3">
                  TrésoPilot ne fournit <strong>aucun conseil fiscal, juridique ou comptable</strong>. 
                </p>
                <ul className="space-y-2 text-slate-700 list-disc pl-6">
                  <li>Les fonctionnalités "Simulations" sont des outils d'aide à la réflexion, 
                  pas des recommandations professionnelles</li>
                  <li>Les suggestions et analyses générées par l'IA sont purement informatives</li>
                  <li>Aucune décision financière ne doit être prise uniquement sur la base des informations fournies 
                  par TrésoPilot</li>
                </ul>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <p className="text-slate-900 font-semibold mb-3">
                  Pour toute question relative à votre situation fiscale ou sociale, consultez :
                </p>
                <ul className="space-y-2 text-slate-700 list-disc pl-6">
                  <li><strong>Un expert-comptable</strong> pour vos questions comptables et fiscales</li>
                  <li><strong>Un conseiller fiscal</strong> pour optimiser votre situation fiscale</li>
                  <li><strong>Un avocat spécialisé</strong> pour les questions juridiques</li>
                  <li><strong>Les services officiels</strong> : URSSAF, Direction des Impôts, CCI, CMA</li>
                </ul>
              </div>
            </section>

            {/* Responsabilité */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Responsabilité de l'utilisateur</h2>
              
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4">
                <p className="text-orange-900 font-semibold mb-2">⚠️ Responsabilité exclusive</p>
                <p className="text-orange-800 leading-relaxed">
                  L'utilisateur reste <strong>seul et entièrement responsable</strong> de l'ensemble de ses obligations 
                  légales, fiscales et sociales. L'utilisation de TrésoPilot ne modifie en rien ces responsabilités.
                </p>
              </div>

              <div className="space-y-3">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-semibold text-slate-900 mb-2">Vous êtes responsable de :</p>
                  <ul className="space-y-2 text-slate-700 list-disc pl-6">
                    <li>L'exactitude et la complétude des informations saisies dans l'application</li>
                    <li>Vos déclarations officielles auprès de l'URSSAF, des impôts et autres administrations</li>
                    <li>Le respect des délais légaux de déclaration et de paiement</li>
                    <li>Le paiement intégral et dans les temps de vos cotisations sociales, impôts et taxes</li>
                    <li>La vérification de vos calculs sur les portails officiels</li>
                    <li>La mise à jour de vos informations en cas de changement de situation</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-semibold text-slate-900 mb-2">Limitation de responsabilité de TrésoPilot :</p>
                  <p className="text-slate-700 leading-relaxed mb-3">
                    Dans les limites autorisées par la loi, TrésoPilot ne peut être tenu responsable :
                  </p>
                  <ul className="space-y-2 text-slate-700 list-disc pl-6">
                    <li>Des erreurs de calcul, omissions ou inexactitudes dans les estimations fournies</li>
                    <li>Des retards de paiement, pénalités ou majorations résultant de l'utilisation de l'application</li>
                    <li>Des conséquences d'une mauvaise saisie de données par l'utilisateur</li>
                    <li>Des changements réglementaires non encore répercutés dans l'application</li>
                    <li>De tout dommage direct ou indirect résultant de l'utilisation ou de l'impossibilité d'utiliser 
                    le service</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Mises à jour et évolutions */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Mises à jour et évolutions réglementaires</h2>
              
              <p className="text-slate-700 leading-relaxed mb-3">
                Les règles fiscales et sociales évoluent régulièrement (lois de finances, décrets, circulaires). 
                TrésoPilot s'efforce de maintenir ses calculs à jour, mais ne peut garantir que tous les changements 
                législatifs sont immédiatement répercutés dans l'application.
              </p>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <p className="text-yellow-900 font-semibold mb-2">📅 Délai de mise à jour</p>
                <p className="text-yellow-800 leading-relaxed">
                  Un délai peut exister entre la publication d'une nouvelle réglementation et sa prise en compte dans 
                  TrésoPilot. En cas de doute, référez-vous toujours aux textes officiels et aux communications des 
                  organismes compétents.
                </p>
              </div>
            </section>

            {/* Protection des données */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Protection des données et confidentialité</h2>
              
              <div className="bg-green-50 rounded-xl p-6">
                <p className="text-slate-700 leading-relaxed mb-3">
                  <strong>Sécurité et confidentialité :</strong>
                </p>
                <ul className="space-y-2 text-slate-700 list-disc pl-6">
                  <li>Vos données financières sont stockées de manière sécurisée et chiffrée</li>
                  <li>Vos données ne sont jamais partagées avec des tiers à des fins commerciales</li>
                  <li>TrésoPilot <strong>ne transmet aucune information</strong> aux administrations fiscales ou sociales</li>
                  <li>Vous restez maître de vos déclarations officielles</li>
                  <li>Vous pouvez supprimer vos données à tout moment</li>
                </ul>
                <p className="text-slate-600 text-sm mt-4">
                  Pour plus d'informations, consultez notre 
                  <a href="/confidentialite" className="text-emerald-600 hover:text-emerald-700 underline ml-1">
                    Politique de confidentialité
                  </a>.
                </p>
              </div>
            </section>

            {/* En résumé */}
            <section className="mb-8">
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-white">
                <h2 className="text-2xl font-bold mb-4">En résumé</h2>
                <p className="leading-relaxed mb-4 text-slate-100">
                  TrésoPilot est un <strong>assistant de gestion de trésorerie</strong> conçu pour vous aider à 
                  anticiper et organiser vos finances de micro-entrepreneur. C'est un outil d'aide à la décision, 
                  <strong> pas un service professionnel</strong>.
                </p>
                <div className="bg-slate-700/50 rounded-xl p-4 mb-4">
                  <p className="text-slate-200 font-semibold mb-2">Il ne remplace pas :</p>
                  <ul className="space-y-1 text-slate-300 list-disc pl-6">
                    <li>Un expert-comptable</li>
                    <li>Un conseiller fiscal</li>
                    <li>Les déclarations officielles obligatoires</li>
                    <li>Les portails URSSAF et impots.gouv.fr</li>
                  </ul>
                </div>
                <p className="text-sm text-slate-300 border-t border-slate-600 pt-4">
                  <strong>En utilisant TrésoPilot</strong>, vous reconnaissez avoir pris connaissance de ces 
                  avertissements et acceptez d'utiliser l'application à titre informatif uniquement, en complément 
                  de vos vérifications sur les portails officiels.
                </p>
              </div>
            </section>

            <div className="border-t border-slate-200 pt-6 mt-8 text-center text-slate-500 text-sm space-y-2">
              <p><Tagline /></p>
              <p>© {new Date().getFullYear()} TrésoPilot. Tous droits réservés.</p>
              <p className="mt-2">
                Document d'avertissement — Dernière mise à jour : 28 décembre 2025
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}